import { memo } from 'react'
import type { MouseEventHandler, ReactNode } from 'react'

import { styled } from '@mui/material/styles'

import { WebDialog } from '~/src/Dialog'
import type { AroyaDialogProps } from '~/src/Dialog'
import getClassPrefixer from '~/src/UI/classPrefixer'
import Button from '~/src/UI/Shared/Button'
import type { AroyaButtonProps } from '~/src/UI/Shared/Button'

const classes = getClassPrefixer('Dialog')

// The blurred backdrop behind it is not styled here: the theme's MuiBackdrop override (src/UI/Theme/mui.js)
// blurs the backdrop of every dialog.
const CenteredDialog = styled((props: AroyaDialogProps) => <WebDialog {...props} />)({
  [`&& .${classes.root}`]: {
    boxShadow: 'none',
    justifyContent: 'center',
    textAlign: 'center',
  },
  [`&& .${classes.content}`]: { flex: '0 1 auto' },
  "&& [data-testid='aroya-close-button']": { display: 'none' },
})

const Row = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  '& .MuiButton-root': { flex: 1 },
}))

interface MobileConfirmationDialogProps extends AroyaDialogProps {
  cancelButtonProps?: AroyaButtonProps | null
  cancelText: ReactNode
  confirmButtonProps?: AroyaButtonProps | null
  confirmText: ReactNode
  destructive: boolean
  disable: boolean
  noCancel: boolean
  onCancel: () => void
  onConfirm: MouseEventHandler<HTMLButtonElement>
}

const MobileConfirmationDialog = memo(
  ({
    actions,
    cancelButtonProps,
    cancelText,
    children,
    confirmButtonProps,
    confirmText,
    content,
    dataTestId,
    destructive,
    disable,
    noCancel,
    onCancel,
    onConfirm,
    ...rest
  }: MobileConfirmationDialogProps & { dataTestId: string }) => (
    <CenteredDialog
      onClose={onCancel}
      {...rest}
      data-testid={dataTestId}
      transparent
      content={
        <>
          {content ?? children}
          {content !== undefined ? children : null}
          <Row>
            {actions || (
              <>
                {noCancel ? null : (
                  <Button
                    color={!destructive ? 'error' : undefined}
                    data-testid="confirm-dialog-no"
                    variant="text"
                    onClick={onCancel}
                    {...cancelButtonProps}
                  >
                    {cancelText}
                  </Button>
                )}
                <Button
                  color={destructive ? 'error' : undefined}
                  data-testid="confirm-dialog-yes"
                  disabled={disable}
                  onClick={onConfirm}
                  {...confirmButtonProps}
                >
                  {confirmText}
                </Button>
              </>
            )}
          </Row>
        </>
      }
    />
  ),
)
MobileConfirmationDialog.displayName = 'MobileConfirmationDialog'

export default MobileConfirmationDialog
